/**
 * Reads a product's 3D-model binding off a loaded config. Deliberately dependency-free: the configurator imports this
 * to decide whether to show "View in 3D" at all, while everything the viewer itself needs (the resolver, the model
 * runtime, three.js) stays in chunks that load on the click.
 */

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const safe = (v) => (v ?? '').toString();
const isGuid = (v) => GUID.test(safe(v));
const guidText = (g) => (isGuid(safe(g)) && !/^0{8}-/.test(safe(g)) ? safe(g).toLowerCase() : null);

/**
 * True when a runtime visualisation (as read by readRuntimeVisualization) carries a model the viewer can run - the
 * client twin of ProductVisualization.HasModel. A binding alone (a blob published before models travelled, or whose
 * model was unpublished at publish) is not a viewer.
 */
export const hasRuntimeVisualization = (v) => !!v && typeof v.Model?.Script === 'string' && !!v.Model.Script.trim();

/** A Bridge Dictionary<string,string>, or a plain object, as a plain object. */
const plainMap = (map) => {
  if (!map || typeof map !== 'object') return null;
  const out = {};
  if (Object.getPrototypeOf(map) === Object.prototype) {
    Object.entries(map).forEach(([k, v]) => { out[safe(k)] = safe(v); });
  } else {
    const from = window.System?.Linq?.Enumerable?.from;
    if (typeof from !== 'function') return null;
    from(map).toArray().forEach((kv) => { out[safe(kv.key ?? kv.Key)] = safe(kv.value ?? kv.Value); });
  }
  return out;
};
const plainArray = (list) => {
  if (!list) return [];
  if (Array.isArray(list)) return list;
  const from = window.System?.Linq?.Enumerable?.from;
  return typeof from === 'function' ? from(list).toArray() : [];
};

/** The embedded model as published with the product ({ TypeVersion, Script, Params, Members }: JSON text), or null. */
const plainModel = (m) => {
  if (!m || typeof m !== 'object') return null;
  const script = safe(m.Script);
  if (!script.trim()) return null;
  return {
    TypeVersion: safe(m.TypeVersion) || null,
    Script: script,
    Params: m.Params == null ? null : safe(m.Params),
    Members: m.Members == null ? null : safe(m.Members),
  };
};

/**
 * The runtime visualisation of a loaded config (InsyteConfigDataModel.Data.Data.Visualization) as a plain object
 * (Guids as text, the value maps as objects - the Bridge runtime hands back its own Guid / Dictionary types), or null
 * when the product carries no visualisation. Model is the published model that travels with the product (what the
 * order line renders; see hasRuntimeVisualization); ModelCustomizationID is provenance only and is absent on a
 * product resold through the marketplace.
 */
export const readRuntimeVisualization = (config) => {
  try {
    const v = config?.Data?.Data?.Visualization ?? null;
    if (!v) return null;
    const modelID = guidText(v.ModelCustomizationID);
    const model = plainModel(v.Model);
    if (!modelID && !model) return null;
    return {
      ModelCustomizationID: modelID,
      ModelName: safe(v.ModelName),
      Model: model,
      Members: plainArray(v.Members).filter(Boolean).map((m) => ({ Role: safe(m.Role), MemberID: guidText(m.MemberID) })),
      Inputs: plainArray(v.Inputs).filter(Boolean).map((i) => ({
        Input: safe(i.Input),
        Source: safe(i.Source),
        Constant: i.Constant ?? null,
        OptionID: guidText(i.OptionID),
        ValueMap: plainMap(i.ValueMap),
        Default: i.Default ?? null,
      })),
    };
  } catch {
    return null;
  }
};
